import { CardContent } from '@mui/material'
import { Spacer, Item } from '../../styles'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { formatDate } from '../formatDate'
import { StyledView, StyledTypography } from '../common/styles'
import { formatDecimal } from '../../../../../services/utils/format-numbers'

/**
 * A component that displays generation plant details for producer contracts
 *
 * @param {object} contract - Contains the producer's contract data from the new API
 * @param {string} contract.plantName - Name of the power plant
 * @param {string} contract.energyPlantType - Type of power plant (SOLAR, WIND, HYDRO, etc.)
 * @param {object} contract.plantAddress - The plant's address information
 * @param {string} contract.plantAddress.street - Street name for the plant
 * @param {string} contract.plantAddress.houseNumber - House number for the plant
 * @param {string} contract.plantAddress.postalCode - Postal/zip code for the plant
 * @param {string} contract.plantAddress.city - City where the plant is located
 * @param {number} contract.installedPowerKw - Installed power capacity in kilowatts
 * @param {string} contract.commissioningDate - Date when the plant was commissioned
 * @param {string} contract.marketLocation - Market location identifier
 * @param {string} contract.meterNumber - Meter identification number
 * @param {string} contract.plantKey - Unique identifier for the plant
 * @param {string} contract.gridOperator - Name of the grid operator
 * @param {string} contract.startOfDeliveryDate - Date when energy delivery starts
 * @param {boolean} contract.isStartOfDeliveryNextPossibleDate - Whether to use next possible date for delivery
 */
function ProducerGenerationPlantDetails({ contract }) {
  const { t } = useTranslation()

  return (
    <>
      <StyledView>
        <CardContent>
          <StyledTypography fontWeight="bold" variant="h4" gutterBottom>
            {t('LBLGenerationPlantDetails')}
          </StyledTypography>
          <Spacer mb={4} />
          <StyledTypography variant="body2" component="div" gutterBottom>
            {/* Name of Power Plant field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPlantName')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.plantName || '--'}
              </StyledTypography>
            </Item>

            {/* Power Plant Type field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPlantType')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.energyPlantType ? t(contract.energyPlantType) : '--'}
              </StyledTypography>
            </Item>

            {/* Plant Address field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPlantAdress')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.plantAddress ? (
                  <>
                    {contract.plantAddress.street &&
                      contract.plantAddress.houseNumber &&
                      `${contract.plantAddress.street} ${contract.plantAddress.houseNumber}`}
                    <br />
                    {contract.plantAddress.postalCode &&
                      contract.plantAddress.city &&
                      `${contract.plantAddress.postalCode} ${contract.plantAddress.city}`}
                  </>
                ) : (
                  '--'
                )}
              </StyledTypography>
            </Item>

            {/* Installed Output field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLInstalledOutput')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.installedPowerKw !== undefined
                  ? `${formatDecimal(contract.installedPowerKw)} kW`
                  : '--'}
              </StyledTypography>
            </Item>

            {/* Commissioning Date field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLCommissioningDate')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.commissioningDate
                  ? formatDate(contract.commissioningDate)
                  : '--'}
              </StyledTypography>
            </Item>

            {/* Start of Delivery field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLStartOfDeliveryDate')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.isStartOfDeliveryNextPossibleDate
                  ? t('NextPossibleDate')
                  : contract?.startOfDeliveryDate
                  ? formatDate(contract.startOfDeliveryDate)
                  : '--'}
              </StyledTypography>
            </Item>

            {/* Market Location field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLMarketLocation')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.marketLocation || '--'}
              </StyledTypography>
            </Item>

            {/* Meter Number field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLMeterNumber')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.meterNumber || '--'}
              </StyledTypography>
            </Item>

            {/* Plant Key field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLPlantKey')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.plantKey || '--'}
              </StyledTypography>
            </Item>

            {/* Operator field */}
            <Item>
              <StyledTypography fontWeight="fontWeightMedium">
                {t('LBLOperator')}
              </StyledTypography>
              <StyledTypography fontWeight="fontWeightRegular" align="right">
                {contract?.gridOperator || '--'}
              </StyledTypography>
            </Item>
          </StyledTypography>
        </CardContent>
      </StyledView>
    </>
  )
}

export default ProducerGenerationPlantDetails
